<template>
    <div class = "shipping-address-view" v-loading = 'loading'>
        <div v-if = "!loading">
            <div class  = "notification-wrapper">
                <!-- show family bundle notification below if is_family_order && !is_family_main_order && show ShippingAddressInputsView -->
                <!-- NOTES: ONLY show for follow-up orders and when shipping address required (should always be required for family bundle orders) -->
                <SimplifiedNotification v-if = "is_family_order && !is_family_main_order && (!is_kit_delivered && !edits_saved && address_needed && !show_contact_support_to_solve_duplicated_patient)"
                title = "This is a Family Discount Order"
                build_in_theme = "blue"
                :with_close_btn = "false"
                content = "We always ship to the base order address for family bundle orders. To update the shipping address, please modify the base order."
                />
                <!-- show change request notification if show ShippingAddressInputsView (editable instead of display ONLY, so use is_editable) && is_kit_shipped -->
                <!-- NOTES: is_kit_shipped will be true ONLY if address_needed, so we skip to check address_needed-->
                <SimplifiedNotification v-if = "is_editable && !edits_saved && !show_contact_support_to_solve_duplicated_patient && is_kit_shipped"
                title = "The order is in progress."
                build_in_theme = "blue"
                :with_close_btn = "false"
                content = "If you still need to update your address, please make the changes and click 'Save'. We'll help submit a change request on your behalf."
                />
            </div>
            <BasicInformationView v-if = "!is_kit_delivered && !edits_saved && !show_contact_support_to_solve_duplicated_patient"/>
            <ShippingAddressInputsView v-if = "!is_kit_delivered && !edits_saved && address_needed && !show_contact_support_to_solve_duplicated_patient" />
            <ChangeAddressStatusView v-if = 'is_kit_delivered || edits_saved || show_contact_support_to_solve_duplicated_patient' />
            <OpsButtonsBar v-if = "!is_kit_delivered && !edits_saved && !show_contact_support_to_solve_duplicated_patient"/>
        </div>
        <!-- VIB-1735 / PRD §4.1 & §4.2: blocking popup for a GZ/GZ-NY address-change mismatch. -->
        <GutZoomerNYBlockDialog
            v-model = "shippingAddressStore.show_gz_ny_block_dialog"
            :direction = "gz_ny_block_direction"
            :patient_state = "gz_ny_block_patient_state"
            @close = "closeGzNyBlockDialog"
        />
    </div>

</template>

<script>
import { useShippingAddressStore } from '@/stores/ShippingAddressStore.ts';
import { useCommonStore } from "@/stores/CommonStore.ts";
import ShippingAddressInputsView from './ShippingAddressInputsView.vue';
import ChangeAddressStatusView from './ChangeAddressStatusView.vue';
import BasicInformationView from "@/components/shippingAddress/BasicInformationView.vue";
import OpsButtonsBar from '@/components/shippingAddress/OpsButtonsBar.vue';
import GutZoomerNYBlockDialog from '@/components/shippingAddress/GutZoomerNYBlockDialog.vue';
// import { env_version } from '@/components/shippingAddress/shippingAddressData/shippingAddressMockData.js';
import { mapState, mapActions } from 'pinia';
import { ElNotification } from 'element-plus';

export default{
    components:{
        ShippingAddressInputsView,
        ChangeAddressStatusView,
        BasicInformationView,
        OpsButtonsBar,
        GutZoomerNYBlockDialog,
    },
    setup(){
        const shippingAddressStore = useShippingAddressStore();
        return {
            shippingAddressStore,
        }
    },
    async mounted(){
        //start loading
        this.shippingAddressStore.loading = true;
        // ------------ remove the logic with unsecured link (Might cause privacy expose) ----------------------
        // console.log('env_version:', process.env, process.env.VUE_APP_ENV);
        // this.setAccessionId(this.$route.query.accession_id);
        // this.setSampleId(this.$route.query.sample_id);
        // this.storeAllQueryInfomation(this.$route.query);
        // ------------ remove the logic with unsecured link (Might cause privacy expose) END ----------------------

        // ------------ the redirect feature if refresh at /informationLanding ----------------------
        try{
            //if token, sample_id or accession_id in ShippingAddressStore are null or empty, means user refresh at the current page:
            // -> need to get the query from the localStorage, and reset variables in store
            if(!this.sample_id || !this.accession_id || !this.token){
                console.log('getLocalStorageQuery ShippingAddressView',localStorage.getItem('pns_single_page_query'));
                // 1. set data in ShippingAddressStore: for page initiation when refresh at /informationLanding
                // NOTES: need to use await, since storeAllQueryInfomation might throw Error
                await this.storeAllQueryInfomation(JSON.parse(localStorage.getItem('pns_single_page_query')));
                // 2. set data in CommonStore: for the top header information and 'pnsToken' in localStorage
                this.setAccessionId(this.accession_id);
                this.setSampleId(this.sample_id);  
                this.setJwtToken(this.token);
            }
            // 3. call the initiateBasicAndShippingInfoPage() to fetch all information needed and the api of checking whether the shipping address is confirmed or saved before at the same time
            await this.initiateBasicAndShippingInfoPage();
        }catch(err){
            console.log('ShippingAddressView mounted error: ', err);
            ElNotification({
                title: 'Error',
                type: 'error',
                customClass: "errorNotification",
                message: err?.message ?? 'Failed to get basic information and shipping address related information.',
                duration: 0,
            });
            this.$router.push('/failed');
        }finally{
            this.shippingAddressStore.loading = false;
        }
    },
    computed:{
        ...mapState(()=>useShippingAddressStore(),[
            'sample_id',
            'accession_id',
            'patient_id',
            'token',
            'is_editable',
            'edits_saved',
            'loading',
            'previous_shipping_address_data',
            'is_kit_delivered',
            'address_needed',
            'incomplete_address_in_db',
            'show_contact_support_to_solve_duplicated_patient',
            'order_source',
            'gz_ny_block_direction',
            'gz_ny_block_patient_state',
            'is_family_order',
            'is_family_main_order'
        ]),
        is_kit_shipped(){
            return this.previous_shipping_address_data?.reason != 'LABEL_NOT_CREATED';
        },
        is_wellProz_order(){
            return this.order_source == "WP";
        },
        is_shopify_order(){
            return this.order_source == "Shopify";
        },
    },
    watch:{
        incomplete_address_in_db(newVal){
            if(newVal!=null){
                this.shippingAddressStore.is_apply_to_future_order = newVal;
            }
            console.log('incomplete_address_in_db change', newVal);
        },
    },
    methods:{
        ...mapActions(()=>useShippingAddressStore(),[
            'storeAllQueryInfomation',
            'initiateBasicAndShippingInfoPage',
            'getBasicInformationAndShippingAddressInfo',
            'isShippingAddressConfirmedOrSavedBefore',
            'getPatientProfileAddress',
            'goToNextStep',
            'closeGzNyBlockDialog'
        ]),
        ...mapActions(()=>useCommonStore(), [
            'setAccessionId',
            'setSampleId',
            'setJwtToken'
        ])
    }
}
</script>

<style scoped>
.shipping-address-view{
    min-height: calc(100vh - 77px);
    padding-bottom: 114px;
    box-sizing:border-box;
}
.notification-wrapper{
    padding-left:314px;
    padding-right:318px;
    display: flex;
    flex-direction: column;
    row-gap: 16px;
}
@media only screen and (max-width: 1260px){
    .notification-wrapper{
        padding-left:157px;
        padding-right:159px;
    }
}
@media only screen and (max-width: 767px){
    .shipping-address-view{
        padding-bottom: 104px;
    }
    .notification-wrapper{
        padding-left:28px;
        padding-right:27px;
        row-gap: 8px;
    }
}
</style>